import React, { useState, useEffect } from 'react'
import useTitle from "../../../hooks/useTitle"
import {
    PlusIcon,
    MinusIcon,
    PencilSquareIcon,
  } from "@heroicons/react/24/outline"
import PulseLoader from "react-spinners/PulseLoader"
import EtContentEditorLight2 from "../../../utils/EtContentEditorLight2"
import EtBtnFormBasic from "../../../components/common/buttons/EtBtnFormBasic"
import { useNavigate } from "react-router"
import { useCreateNewUserImprintCapabilitiesAdvisoryMutation } from './userImprintCapabilitiesAdvisorysApiSlice'
import { useGetCapabilitiesAdvisoryTypesQuery } from '../../capabilitiesAdvisoryTypes/capabilitiesAdvisoryTypesApiSlice'

const EtCreateUserImprintCapabilitiesAdvisoryComp = ({
  userId, 
  userOrgId, 
  triggeringUrl, 
  onClose
}) => {
  // First Created - 22 Nov 2024
  // Author - Atul Pandey
  // Inherited from the EtCreateUserImprintCapabilitiesIndustry component

  // 0 - Set Page title & variables
  useTitle('User Imprint Cpabilities')

  // 1 - set page navigations
  const navigate = useNavigate()
  
  // 2a - set the mutation to create the new user imprint capabilitiesAdvisory data
  const [createNewUserImprintCapabilitiesAdvisory, {
    isLoading,
    isSuccess,
    isError,
    error,
  }] = useCreateNewUserImprintCapabilitiesAdvisoryMutation()

  // 2b - Get capability advisory types
  const { data: advisoryTypesData} = useGetCapabilitiesAdvisoryTypesQuery()
  const advisoryTypes = advisoryTypesData?.ids?.map(id => advisoryTypesData?.entities[id])
  // console.log('Advisory Types', advisoryTypes)

  // // 2c - Group Advisory Types by focus, domain, function, and tech area
  // const groupedAdvisoryTypes = advisoryTypes?.reduce((result, type) => {
  // const focus = type.capabilitiesTechAreaType?.capabilitiesFunctionType?.capabilitiesDomainType?.capabilitiesDomainFocus || "Other";
  // const domainName = type.capabilitiesTechAreaType?.capabilitiesFunctionType?.capabilitiesDomainType?.capabilitiesDomainTypeName || "Unknown Domain";
  // const functionName = type.capabilitiesTechAreaType?.capabilitiesFunctionType?.capabilitiesFunctionTypeName || "Unknown Function";
  // const techAreaName = type.capabilitiesTechAreaType?.capabilitiesTechAreaTypeName || "Unknown TechArea";

  // if (!result[focus]) {
  //   result[focus] = {};
  // }

  // if (!result[focus][domainName]) {
  //   result[focus][domainName] = {};
  // }

  // if (!result[focus][domainName][functionName]) {
  //   result[focus][domainName][functionName] = {};
  // }

  // if (!result[focus][domainName][functionName][techAreaName]) {
  //   result[focus][domainName][functionName][techAreaName] = [];
  // }

  // result[focus][domainName][functionName][techAreaName].push(type);

  // return result;
  // }, {}) || {};

  // console.log('groupedAdvisoryTypes', groupedAdvisoryTypes);

  // 3 - Initialize state variables for the form data
  const [formData, setFormData] = useState({
    userImprintCapabilitiesAdvisoryName: "",
    userImprintCapabilitiesAdvisoryDesc: "",
    userImprintCapabilitiesAdvisoryStats: [
      {
        advisoryType:null,
        yearsOfExperience: 0,
        proficiencyLevel: "Yet to Begin",
        numberOfEngagements:0,
        certifications: [
          {
            certificationName: "",
            certificationDesc: "",
            certificationOrg:"",
            certificationDate: "",
            validTill: "",
          }
        ],
        recognitions: [
          {
            recognitionName: "",
            recognitionDesc: "",
            recognitionOrg: "",
            recognitionDate: "",
          }
        ],
      }
    ],
    userImprintCapabilitiesAdvisorySelfNotes: [],
    aboutMe: "",
    pic: "",
  })

  // 4 - Initialize state variables for the editor key and value
  const [editorKey, setEditorKey] = useState("capabilitiesAdvisoryDesc-" + Date.now())
  const [editorValue, setEditorValue] = useState("")
  const [showCreateUserImprintCapabilitiesAdvisory, setShowCreateUserImprintCapabilitiesAdvisory] = useState(false)
  
  // 5 - Handle form field changes
  const handleChange = (e) => {
    const { name, value, files } = e.target
    if (name === "pic") {
      setFormData(prevState => ({ ...prevState, pic: files[0] }))
    } else {
      setFormData(prevState => ({ ...prevState, [name]: value }))
    }
  }

  // 5a - Handle editor value change
  const handleEditorChange = (content) => {
    setEditorValue(content)
    setFormData(prevState => ({ ...prevState, userImprintCapabilitiesAdvisoryDesc: content }))
  }

  // 5b - Generic Handle Add Field Advisory
  const handleAdd = (fieldName, newItem) => {
    setFormData(prevState => ({
      ...prevState,
      [fieldName]: [...prevState[fieldName], newItem]
    }))
  }

  // 5c - Generic Handle Remove Field Advisory
  const handleRemove = (fieldName, index) => {
    setFormData(prevState => ({
      ...prevState,
      [fieldName]: prevState[fieldName].filter((_, i) => i !== index)
    }))
  }

  // 5d - Advisory to convert pic to Base64
  const convertPicToBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
      reader.readAsDataURL(file);
    }); 
 

  // 6 - Form submit logic
  const handleSubmit = async (e) => {
    e.preventDefault();
  
    // Step 1: Construct the payload and filter out empty values
    const payload = {
      userId,
      userOrgId,
      userImprintCapabilitiesAdvisoryName: formData.userImprintCapabilitiesAdvisoryName?.trim() || undefined,
      userImprintCapabilitiesAdvisoryDesc: formData.userImprintCapabilitiesAdvisoryDesc?.trim() || undefined,
      userImprintCapabilitiesAdvisoryStats: formData.userImprintCapabilitiesAdvisoryStats.map((stat) => ({
        ...stat,
        certifications: stat.certifications.filter((cert) =>
          Object.values(cert).some((value) => value !== "")
        ),
        recognitions: stat.recognitions.filter((rec) =>
          Object.values(rec).some((value) => value !== "")
        ),
      })),
      userImprintCapabilitiesAdvisorySelfNotes: formData.userImprintCapabilitiesAdvisorySelfNotes.filter(
        (note) => note.userImprintCapabilitiesAdvisorySelfNoteText.trim() !== ""
      ),
      aboutMe: formData.aboutMe?.trim() || undefined,
      pic: formData.pic ? await convertPicToBase64(formData.pic) : undefined, // Convert pic to Base64 if provided
    };
  
    try {
      // Step 2: Call the mutation
      await createNewUserImprintCapabilitiesAdvisory(payload).unwrap();
  
      // Step 3: Reset the form and handle success
      setFormData({
        userImprintCapabilitiesAdvisoryName: "",
        userImprintCapabilitiesAdvisoryDesc: "",
        userImprintCapabilitiesAdvisoryStats: [
          {
            advisoryType: null,
            yearsOfExperience: 0,
            proficiencyLevel: "Yet to Begin",
            numberOfEngagements: 0,
            certifications: [],
            recognitions: [],
          },
        ],
        userImprintCapabilitiesAdvisorySelfNotes: [],
        aboutMe: "",
        pic: "",
      });
      if (onClose) {
        onClose(); // 21 Nov 2024
      } else{
        navigate(triggeringUrl || '/userMagicHome');
      }
      
    } catch (error) {
      console.error("Error submitting form:", error);
    }
  };
  
  

  // 7 - Reset form upon successful submission
  useEffect(() => {
    if(isSuccess) {
      setFormData({
        userImprintCapabilitiesAdvisoryName: "",
      userImprintCapabilitiesAdvisoryDesc: "",
      userImprintCapabilitiesAdvisoryStats: [
        {
          advisoryType:null,
          yearsOfExperience: 0,
          proficiencyLevel: "Yet to Begin",
          numberOfEngagements:0,
          certifications: [
            {
              certificationName: "",
              certificationDesc: "",
              certificationOrg:"",
              certificationDate: "",
              validTill: "",
            }
          ],
          recognitions: [
            {
              recognitionName: "",
              recognitionDesc: "",
              recognitionOrg: "",
              recognitionDate: "",
            }
          ],
        }
      ],
      userImprintCapabilitiesAdvisorySelfNotes: [],
      aboutMe: "",
      pic: "",
      })
      // setShowCreateUserImprintCapabilitiesAdvisory(false)
      if (onClose) {
        onClose(); // 21 Nov 2024
      } else{
        navigate(triggeringUrl || '/userMagicHome');
      }
    }
  
    },[isSuccess,navigate])

  // 8 - Handle Cancel
  const handleCancel = () => {
    // setShowCreateUserImprintCapabilitiesAdvisory(false)
    if (onClose) {
      onClose(); // 21 Nov 2024
    } else{
      navigate(triggeringUrl || '/userMagicHome');
    }
  }

  // 9 - Handle Clear
  const handleClear = () => {
    setFormData({
      userImprintCapabilitiesAdvisoryName: "",
      userImprintCapabilitiesAdvisoryDesc: "",
      userImprintCapabilitiesAdvisoryStats: [
        {
          advisoryType:null,
          yearsOfExperience: 0,
          proficiencyLevel: "Yet to Begin",
          numberOfEngagements:0,
          certifications: [
            {
              certificationName: "",
              certificationDesc: "",
              certificationOrg:"",
              certificationDate: "",
              validTill: "",
            }
          ],
          recognitions: [
            {
              recognitionName: "",
              recognitionDesc: "",
              recognitionOrg: "",
              recognitionDate: "",
            }
          ],
        }
      ],
      userImprintCapabilitiesAdvisorySelfNotes: [],
      aboutMe: "",
      pic: "",
    })
  }

  return (
    <div className="mx-auto w-full m-1 p-1 bg-white rounded shadow-md shadow-black">
      {isLoading && <PulseLoader color={"#ea580c"} />}
      {isError && (
        <p className="text-orange-700 font-semi-bold py-2">
          {error?.data?.message}
        </p>
      )}

      <p className="p-1 m-1 font-bold font-poppins text-xl">Create User Imprint Capabilities - Advisory</p>

      
      <form onSubmit={handleSubmit} className="m-1 p-1 text-xs font-poppins">
      {/* 1. userImprintCapabilitiesAdvisoryName */}
      <div id="userImprintCapabilitiesAdvisoryName" className="bg-gray-100 rounded p-1 m-1">
        <label htmlFor="userImprintCapabilitiesAdvisoryName" className="block text-gray-600 text-sm font-bold mb-1">
          Capability Name (Optional)
        </label>
        <input
          type="text"
          name="userImprintCapabilitiesAdvisoryName"
          placeholder="Enter a headline or title"
          value={formData.userImprintCapabilitiesAdvisoryName}
          onChange={handleChange}
          className="w-full rounded p-2 border border-gray-300 focus:ring-orange-500 focus:border-orange-500"
        />
      </div>
      
      {/* 2. userImprintCapabilitiesAdvisoryDesc */}
      <div id="userImprintCapabilitiesAdvisoryDesc" className="bg-gray-100 rounded p-1 m-1">
        <label htmlFor="userImprintCapabilitiesAdvisoryDesc" className="block text-gray-600 text-sm font-bold mb-1">
          Capability Description
        </label>
        <EtContentEditorLight2
          value={editorValue}
          handleChange={handleEditorChange}
          placeholderText="Write your capability description here!"
          editorKey={editorKey}
        />
      </div>

      {/* Advisory Stats Section */}

      <div className="flex justify-between items-center m-1 p-1">
        <p className="font-bold text-sm mb-2">Advisory Stats</p>
        <div className='flex justify-start items-center'>
          <p className='text-xs font-bold mx-1 px-1'>Add</p>
          <button
            type="button"
            className="bg-lime-600 hover:bg-lime-700 text-white text-xs font-bold m-1 p-1 rounded"
            onClick={() => {
              setFormData((prevState) => ({
                ...prevState,
                userImprintCapabilitiesAdvisoryStats: [
                  ...prevState.userImprintCapabilitiesAdvisoryStats,
                  {
                    advisoryType: null,
                    yearsOfExperience: 0,
                    proficiencyLevel: "Yet to Begin",
                    numberOfEngagements: 0,
                    certifications: [],
                    recognitions: [],
                  },
                ],
              }));
            }}
          >
            <PlusIcon className="h-4 w-4 inline" /> 
          </button>
        </div>
        
      </div>

      {formData.userImprintCapabilitiesAdvisoryStats.map((stat, statIndex) => (
          <div key={statIndex} className="border rounded p-2 m-2 bg-gray-50">
            <div className="flex justify-between items-center mb-2">
                <p className="font-bold text-sm mb-2">Advisory Stat {statIndex + 1}</p>
                <div className='flex justify-start items-center'>
                  <p className='text-xs font-bold mx-1 px-1'>Remove</p>
                  <button
                    type="button"
                    className="bg-red-500 hover:bg-red-600 text-white text-xs font-bold m-1 p-1 rounded"
                    onClick={() => {
                      setFormData((prevState) => {
                        const updatedStats = [...prevState.userImprintCapabilitiesAdvisoryStats];
                        updatedStats.splice(statIndex, 1); // Remove the selected stats
                        return { ...prevState, userImprintCapabilitiesAdvisoryStats: updatedStats };
                      });
                    }}
                  >
                    <MinusIcon className="h-4 w-4 inline" /> 
                  </button>
                </div>
                
            </div>

           

            {/* Advisory Type Dropdown */}
            <div id={`advisoryType-${statIndex}`} className="flex justify-between items-center bg-gray-100 rounded p-1 m-1">
              <label htmlFor={`advisoryType-${statIndex}`} className="text-gray-600 text-sm font-bold w-1/3">
                Advisory Type
              </label>
                <select
                id={`advisoryType-${statIndex}`}
                value={stat.advisoryType || ""}
                onChange={(e) => {
                  const value = e.target.value;
                  setFormData((prevState) => {
                    const updatedStats = [...prevState.userImprintCapabilitiesAdvisoryStats];
                    updatedStats[statIndex].advisoryType = value;
                    return { ...prevState, userImprintCapabilitiesAdvisoryStats: updatedStats };
                  });
                }}
                className="w-2/3 rounded p-2 border border-gray-300 focus:outline-none"
              >
                <option value="" disabled>
                  Select Advisory Type
                </option>
                {advisoryTypes?.map((type) => (
                  <option key={type._id} value={type._id}>
                    {type.capabilitiesAdvisoryTypeName}
                  </option>
                ))}
                </select>
                  {/* <select
                    id={`advisoryType-${statIndex}`}
                    value={stat.advisoryType || ""}
                    onChange={(e) => {
                      const value = e.target.value;
                      setFormData((prevState) => {
                        const updatedStats = [...prevState.userImprintCapabilitiesAdvisoryStats];
                        updatedStats[statIndex].advisoryType = value;
                        return { ...prevState, userImprintCapabilitiesAdvisoryStats: updatedStats };
                      });
                    }}
                    className="w-full rounded p-2 border border-gray-300 focus:outline-none"
                  >
                    <option value="" disabled>
                      Select Tech Stack Type
                    </option>
                    {Object.entries(groupedAdvisoryTypes)?.map(([focus, domains]) =>
                      Object.entries(domains).flatMap(([domainName, functions]) =>
                        Object.entries(functions).flatMap(([functionName, techAreas]) =>
                          Object.entries(techAreas).map(([techAreaName, advisorys]) => (
                            <optgroup
                              key={`${focus}-${domainName}-${functionName}-${techAreaName}`}
                              label={`${focus} - ${domainName} - ${functionName} - ${techAreaName}`}
                            >
                              {advisorys.map((stack) => (
                                <option key={stack._id} value={stack._id}>
                                  {stack.capabilitiesAdvisoryTypeName}
                                </option>
                              ))}
                            </optgroup>
                          ))
                        )
                      )
                    )}
                  </select> */}

                  
            </div>

              {/* Years of Experience */}
              <div id={`yearsOfExperience-${statIndex}`} className="flex justify-between items-center bg-gray-100 rounded p-1 m-1">
                <label htmlFor={`yearsOfExperience-${statIndex}`} className="text-gray-600 text-sm font-bold w-1/3">
                  Years of Experience
                </label>
                <input
                  type="number"
                  id={`yearsOfExperience-${statIndex}`}
                  name="yearsOfExperience"
                  min="0"
                  value={stat.yearsOfExperience || 0}
                  onChange={(e) => {
                    const value = parseInt(e.target.value, 10) || 0;
                    setFormData((prevState) => {
                      const updatedStats = [...prevState.userImprintCapabilitiesAdvisoryStats];
                      updatedStats[statIndex].yearsOfExperience = value;
                      return { ...prevState, userImprintCapabilitiesAdvisoryStats: updatedStats };
                    });
                  }}
                  className="w-2/3 rounded p-2 border border-gray-300 focus:outline-none"
                />
              </div>

              {/* Proficiency Level */}
              <div id={`proficiencyLevel-${statIndex}`} className="flex justify-between items-center bg-gray-100 rounded p-1 m-1">
                <label htmlFor={`proficiencyLevel-${statIndex}`} className="text-gray-600 text-sm font-bold w-1/3">
                  Proficiency Level
                </label>
                <select
                  id={`proficiencyLevel-${statIndex}`}
                  name="proficiencyLevel"
                  value={stat.proficiencyLevel || "Yet to Begin"}
                  onChange={(e) => {
                    const value = e.target.value;
                    setFormData((prevState) => {
                      const updatedStats = [...prevState.userImprintCapabilitiesAdvisoryStats];
                      updatedStats[statIndex].proficiencyLevel = value;
                      return { ...prevState, userImprintCapabilitiesAdvisoryStats: updatedStats };
                    });
                  }}
                  className="w-2/3 rounded p-2 border border-gray-300 focus:outline-none"
                >
                  <option value="Yet to Begin">Yet to Begin</option>
                  <option value="Beginner">Beginner</option>
                  <option value="Advanced">Advanced</option>
                  <option value="Pro">Pro</option>
                  <option value="Expert">Expert</option>
                  <option value="Leader">Leader</option>
                </select>
              </div>

              {/* Number of Engagements */}
              <div id={`numberOfEngagements-${statIndex}`} className="flex justify-between items-center bg-gray-100 rounded p-1 m-1">
                <label htmlFor={`numberOfEngagements-${statIndex}`} className="text-gray-600 text-sm font-bold w-1/3">
                  Number of Engagements
                </label>
                <input
                  type="number"
                  id={`numberOfEngagements-${statIndex}`}
                  name="numberOfEngagements"
                  min="0"
                  value={stat.numberOfEngagements || 0}
                  onChange={(e) => {
                    const value = parseInt(e.target.value, 10) || 0;
                    setFormData((prevState) => {
                      const updatedStats = [...prevState.userImprintCapabilitiesAdvisoryStats];
                      updatedStats[statIndex].numberOfEngagements = value;
                      return { ...prevState, userImprintCapabilitiesAdvisoryStats: updatedStats };
                    });
                  }}
                  className="w-2/3 rounded p-2 border border-gray-300 focus:outline-none"
                />
              </div>

              {/* Certifications Section */}
              <div id={`certifications-${statIndex}`} className="border rounded p-2 m-2 bg-gray-50">
                <div className="flex justify-between items-center mb-2">
                    <p className="font-bold text-sm">Certifications</p>
                    <div className='flex justify-start items-center'>
                      <p className='text-xs font-bold mx-1 px-1'>Add</p>
                      <button
                          type="button"
                          className="bg-lime-600 hover:bg-lime-700 text-white text-xs font-bold m-1 p-1 rounded"
                          onClick={() => {
                            setFormData((prevState) => {
                              const updatedStats = [...prevState.userImprintCapabilitiesAdvisoryStats];
                              updatedStats[statIndex].certifications.push({
                                certificationName: "",
                                certificationDesc: "",
                                certificationOrg:"",
                                certificationDate: "",
                                validTill: "",
                              });
                              return { ...prevState, userImprintCapabilitiesAdvisoryStats: updatedStats };
                            });
                          }}
                        >
                        <PlusIcon className="h-4 w-4 inline" />
                      </button>

                    </div>
                  
                </div>

                {/* Certifications List */}
                {stat.certifications.map((cert, certIndex) => (
                  <div key={`${statIndex}-${certIndex}`} className="p-2 border rounded mb-2 bg-white">
                    <div className="flex justify-between items-center">
                        <p className="font-bold text-sm">Certification {certIndex + 1}</p>
                        <div className='flex justify-start items-center'>
                            <p className='text-xs font-bold mx-1 px-1'>Remove</p>
                            <button
                            type="button"
                            className="bg-red-500 hover:bg-red-600 text-white text-xs font-bold m-1 p-1 rounded"
                            onClick={() => {
                              setFormData((prevState) => {
                                const updatedStats = [...prevState.userImprintCapabilitiesAdvisoryStats];
                                updatedStats[statIndex].certifications.splice(certIndex, 1);
                                return { ...prevState, userImprintCapabilitiesAdvisoryStats: updatedStats };
                              });
                            }}
                          >
                            <MinusIcon className="h-4 w-4 inline" />
                          </button>
                        </div>
                      
                    </div>

                    {/* Certification Fields */}
                    <input
                      type="text"
                      placeholder="Certification Name"
                      value={cert.certificationName}
                      onChange={(e) => {
                        const value = e.target.value;
                        setFormData((prevState) => {
                          const updatedStats = [...prevState.userImprintCapabilitiesAdvisoryStats];
                          updatedStats[statIndex].certifications[certIndex].certificationName = value;
                          return { ...prevState, userImprintCapabilitiesAdvisoryStats: updatedStats };
                        });
                      }}
                      className="w-full rounded p-2 border border-gray-300 focus:outline-none focus:ring focus:ring-orange-300 mt-2"
                    />

                    <EtContentEditorLight2
                      value={cert.certificationDesc}
                      handleChange={(content) => {
                        setFormData((prevState) => {
                          const updatedStats = [...prevState.userImprintCapabilitiesAdvisoryStats];
                          updatedStats[statIndex].certifications[certIndex].certificationDesc = content;
                          return { ...prevState, userImprintCapabilitiesAdvisoryStats: updatedStats };
                        });
                      }}
                      placeholderText="Write certification details here..."
                      editorKey={`certificationDesc-${statIndex}-${certIndex}`}
                    />

                    <div className="flex justify-between items-center">
                      <div className="flex flex-col m-1 p-1 w-full">
                        <label
                          htmlFor={`certificationOrg-${statIndex}-${certIndex}`}
                          className="text-gray-600 text-sm font-bold"
                        >
                          Certifying Organization:
                        </label>
                        <input
                          type="text"
                          id={`certificationOrg-${statIndex}-${certIndex}`}
                          value={cert.certificationOrg}
                          onChange={(e) => {
                            const value = e.target.value;
                            setFormData((prevState) => {
                              const updatedStats = [...prevState.userImprintCapabilitiesAdvisoryStats];
                              updatedStats[statIndex].certifications[certIndex].certificationOrg = value;
                              return { ...prevState, userImprintCapabilitiesAdvisoryStats: updatedStats };
                            });
                          }}
                          className="w-full rounded p-2 border border-gray-300 focus:outline-none focus:ring focus:ring-orange-300"
                        />
                      </div>

                      <div className="flex flex-col m-1 p-1 w-full">
                        <label
                          htmlFor={`certificationDate-${statIndex}-${certIndex}`}
                          className="text-gray-600 text-sm font-bold"
                        >
                          Certification Date
                        </label>
                        <input
                          type="date"
                          id={`certificationDate-${statIndex}-${certIndex}`}
                          value={cert.certificationDate}
                          onChange={(e) => {
                            const value = e.target.value;
                            setFormData((prevState) => {
                              const updatedStats = [...prevState.userImprintCapabilitiesAdvisoryStats];
                              updatedStats[statIndex].certifications[certIndex].certificationDate = value;
                              return { ...prevState, userImprintCapabilitiesAdvisoryStats: updatedStats };
                            });
                          }}
                          className="w-full rounded p-2 border border-gray-300 focus:outline-none focus:ring focus:ring-orange-300"
                        />
                      </div>

                      <div className="flex flex-col m-1 p-1 w-full">
                        <label
                          htmlFor={`validTill-${statIndex}-${certIndex}`}
                          className="text-gray-600 text-sm font-bold"
                        >
                          Valid Till
                        </label>
                        <input
                          type="date"
                          id={`validTill-${statIndex}-${certIndex}`}
                          value={cert.validTill}
                          onChange={(e) => {
                            const value = e.target.value;
                            setFormData((prevState) => {
                              const updatedStats = [...prevState.userImprintCapabilitiesAdvisoryStats];
                              const selectedCert = updatedStats[statIndex].certifications[certIndex];
                              if (new Date(value) >= new Date(selectedCert.certificationDate)) {
                                selectedCert.validTill = value;
                              }
                              return { ...prevState, userImprintCapabilitiesAdvisoryStats: updatedStats };
                            });
                          }}
                          className="w-full rounded p-2 border border-gray-300 focus:outline-none focus:ring focus:ring-orange-300"
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Recognitions Section */}
              <div id={`recognitions-${statIndex}`} className="border rounded p-2 m-2 bg-gray-50">
                <div className="flex justify-between items-center mb-2">
                    <p className="font-bold text-sm">Recognitions</p>
                    <div className='flex justify-start items-center'>
                        <p className='text-xs font-bold mx-1 px-1'>Add</p>
                        <button
                          type="button"
                          className="bg-lime-600 hover:bg-lime-700 text-white text-xs font-bold m-1 p-1 rounded"
                          onClick={() => {
                            setFormData((prevState) => {
                              const updatedStats = [...prevState.userImprintCapabilitiesAdvisoryStats];
                              updatedStats[statIndex].recognitions.push({
                                recognitionName: "",
                                recognitionDesc: "",
                                recognitionOrg: "",
                                recognitionDate: "",
                              });
                              return { ...prevState, userImprintCapabilitiesAdvisoryStats: updatedStats };
                            });
                          }}
                        >
                          <PlusIcon className="h-4 w-4 inline" />
                        </button>
                    </div>
                  
                </div>

                {/* Recognitions List */}
                {stat.recognitions.map((rec, recIndex) => (
                  <div key={`${statIndex}-${recIndex}`} className="p-2 border rounded mb-2 bg-white">
                    <div className="flex justify-between items-center">
                        <p className="font-bold text-sm">Recognition {recIndex + 1}</p>
                        <div className='flex justify-start items-center'>
                            <p className='text-xs font-bold mx-1 px-1'>Remove</p>
                            <button
                            type="button"
                            className="bg-red-500 hover:bg-red-600 text-white text-xs font-bold m-1 p-1 rounded"
                            onClick={() => {
                              setFormData((prevState) => {
                                const updatedStats = [...prevState.userImprintCapabilitiesAdvisoryStats];
                                updatedStats[statIndex].recognitions.splice(recIndex, 1);
                                return { ...prevState, userImprintCapabilitiesAdvisoryStats: updatedStats };
                              });
                            }}
                          >
                            <MinusIcon className="h-4 w-4 inline" />
                          </button>
                        </div>

                      
                    </div>

                    {/* Recognition Fields */}
                    <input
                      type="text"
                      placeholder="Recognition Name"
                      value={rec.recognitionName}
                      onChange={(e) => {
                        const value = e.target.value;
                        setFormData((prevState) => {
                          const updatedStats = [...prevState.userImprintCapabilitiesAdvisoryStats];
                          updatedStats[statIndex].recognitions[recIndex].recognitionName = value;
                          return { ...prevState, userImprintCapabilitiesAdvisoryStats: updatedStats };
                        });
                      }}
                      className="w-full rounded p-2 border border-gray-300 focus:outline-none focus:ring focus:ring-orange-300 mt-2"
                    />

                    <EtContentEditorLight2
                      value={rec.recognitionDesc}
                      handleChange={(content) => {
                        setFormData((prevState) => {
                          const updatedStats = [...prevState.userImprintCapabilitiesAdvisoryStats];
                          updatedStats[statIndex].recognitions[recIndex].recognitionDesc = content;
                          return { ...prevState, userImprintCapabilitiesAdvisoryStats: updatedStats };
                        });
                      }}
                      placeholderText="Write recognition details here..."
                      editorKey={`recognitionDesc-${statIndex}-${recIndex}`}
                    />

                    <div className="flex justify-between items-center">
                      <div className="flex flex-col m-1 p-1 w-full">
                        <label
                          htmlFor={`recognitionOrg-${statIndex}-${recIndex}`}
                          className="text-gray-600 text-sm font-bold"
                        >
                          Recognizing Org
                        </label>
                        <input
                          type="text"
                          id={`recognitionOrg-${statIndex}-${recIndex}`}
                          placeholder="Enter recognizing organization"
                          value={rec.recognitionOrg}
                          onChange={(e) => {
                            const value = e.target.value;
                            setFormData((prevState) => {
                              const updatedStats = [...prevState.userImprintCapabilitiesAdvisoryStats];
                              updatedStats[statIndex].recognitions[recIndex].recognitionOrg = value;
                              return { ...prevState, userImprintCapabilitiesAdvisoryStats: updatedStats };
                            });
                          }}
                          className="w-full rounded p-2 border border-gray-300 focus:outline-none focus:ring focus:ring-orange-300"
                        />
                      </div>

                      <div className="flex flex-col m-1 p-1 w-full">
                        <label
                          htmlFor={`recognitionDate-${statIndex}-${recIndex}`}
                          className="text-gray-600 text-sm font-bold"
                        >
                          Recognition Date
                        </label>
                        <input
                          type="date"
                          id={`recognitionDate-${statIndex}-${recIndex}`}
                          value={rec.recognitionDate}
                          onChange={(e) => {
                            const value = e.target.value;
                            setFormData((prevState) => {
                              const updatedStats = [...prevState.userImprintCapabilitiesAdvisoryStats];
                              updatedStats[statIndex].recognitions[recIndex].recognitionDate = value;
                              return { ...prevState, userImprintCapabilitiesAdvisoryStats: updatedStats };
                            });
                          }}
                          className="w-full rounded p-2 border border-gray-300 focus:outline-none focus:ring focus:ring-orange-300"
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
          </div>
      ))}
   
      {/* Notes Section */}
      <div id="selfNotes" className="border rounded p-2 m-2 bg-gray-50">
        <div className="flex justify-between items-center mb-2">
            <p className="font-bold text-sm">Notes</p>
            <div className='flex justify-start items-center'>
                <p className='text-xs font-bold mx-1 px-1'>Add</p>
                <button
                type="button"
                className="bg-lime-600 hover:bg-lime-700 text-white text-xs font-bold m-1 p-1 rounded"
                onClick={() => {
                  setFormData((prevState) => {
                    // console.log("prevState:", prevState);
                    return {
                      ...prevState,
                      userImprintCapabilitiesAdvisorySelfNotes: [
                        ...(Array.isArray(prevState.userImprintCapabilitiesAdvisorySelfNotes)
                          ? prevState.userImprintCapabilitiesAdvisorySelfNotes
                          : []),
                        {
                          userImprintCapabilitiesAdvisorySelfNoteText: "",
                          userImprintCapabilitiesAdvisorySelfNoteDate: new Date().toISOString(),
                          userImprintCapabilitiesAdvisorySelfNoteUpdateDate: null,
                        },
                      ],
                    };
                  });
                }}
              >
                <PlusIcon className="h-4 w-4 inline" />
              </button>
            </div>
          
        </div>

        {formData.userImprintCapabilitiesAdvisorySelfNotes?.map((note, noteIndex) => (
          <div key={noteIndex} className="p-2 border rounded mb-2 bg-white">
            <div className="flex justify-between items-center">
              <p className="font-bold text-sm">Self Note: {noteIndex + 1}</p>
              <button
                type="button"
                className="bg-red-500 hover:bg-red-600 text-white text-xs font-bold py-1 px-2 rounded"
                onClick={() => {
                  setFormData((prevState) => ({
                    ...prevState,
                    userImprintCapabilitiesAdvisorySelfNotes: prevState.userImprintCapabilitiesAdvisorySelfNotes.filter(
                      (_, i) => i !== noteIndex
                    ),
                  }));
                }}
              >
                <MinusIcon className="h-4 w-4 inline" /> Remove
              </button>
            </div>

            {/* Note Text */}
            <EtContentEditorLight2
              value={note.userImprintCapabilitiesAdvisorySelfNoteText}
              handleChange={(content) => {
                setFormData((prevState) => {
                  const updatedNotes = [...prevState.userImprintCapabilitiesAdvisorySelfNotes];
                  updatedNotes[noteIndex].userImprintCapabilitiesAdvisorySelfNoteText = content;
                  updatedNotes[noteIndex].userImprintCapabilitiesAdvisorySelfNoteUpdateDate = new Date().toISOString();
                  return { ...prevState, userImprintCapabilitiesAdvisorySelfNotes: updatedNotes };
                });
              }}
              placeholderText="Write your note here..."
              editorKey={`note-${noteIndex}`}
            />

            {/* Note Date (Read-Only) */}
            <p className="text-xs text-gray-500 mt-2">
              Created on: {new Date(note.userImprintCapabilitiesAdvisorySelfNoteDate).toLocaleDateString()}
              {note.userImprintCapabilitiesAdvisorySelfNoteUpdateDate && (
                <span>
                  , Updated on: {new Date(note.userImprintCapabilitiesAdvisorySelfNoteUpdateDate).toLocaleDateString()}
                </span>
              )}
            </p>
          </div>
        ))}
      </div>

      <div id="actions" className="bg-white m-1 px-1 py-3 rounded">
              <EtBtnFormBasic
                name={"Create"}
                type={"create"}
                handleClick={handleSubmit}
              />
              <EtBtnFormBasic
                name={"Clear"}
                type={"clear"}
                handleClick={handleClear}
              />
              <EtBtnFormBasic
                name={"Cancel"}
                type={"cancel"}
                handleClick={handleCancel}
              />
            </div>

    </form>

    </div>
  )
}

export default EtCreateUserImprintCapabilitiesAdvisoryComp